import { DOCUMENT } from '@angular/common';
import { Component, HostListener, Inject, OnDestroy, OnInit } from '@angular/core';
import { Router, RouterModule, RoutesRecognized } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Store } from '@ngrx/store';
import { filter, pairwise, take } from 'rxjs';
import * as InAppNotificationsActions from 'src/app/in-app-notifications/store/actions/in-app-notifications.actions';
import { MenuItemDirective } from 'src/app/nav-menu/menu-item.directive';
import { NavMenuComponent } from 'src/app/nav-menu/nav-menu.component';
import { TopNavMenuComponent } from 'src/app/nav-menu/top-nav-menu/top-nav-menu.component';
import * as HubActions from 'src/app/root-store/hub-connection-store/hub-connection.actions';
import { NewVersionMessageComponent } from 'src/app/shared/components/new-version-message/new-version-message.component';
import { IfLDFeatureDirective } from 'src/app/shared/directives/if-ld-feature.directive';
import { LDFeatureManager } from 'src/app/shared/services/ld-feature-manager';
import { IdentityService } from './core/identity.service';
import { UserMonitoringService } from './core/user-monitoring.service';
import { selectHubConnectionActive } from './root-store/hub-connection-store/hub-connection.selectors';
import { RecruitingActions } from './root-store/recruiting-store/actions';
import { FeatureFlag } from './shared/models/enums/feature-flag';
import { ConnectionQualityService } from './shared/services/connection-quality/connection-quality.service';
import { PendoService } from './shared/services/pendo.service';
import { PreviousRouteService } from './shared/services/previous-route.service';
import { CdkScrollable } from '@angular/cdk/scrolling';

@UntilDestroy()
@Component({
    selector: 'nova-app',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
    standalone: true,
    imports: [
        MenuItemDirective,
        IfLDFeatureDirective,
        NewVersionMessageComponent,
        RouterModule,
        TopNavMenuComponent,
        NavMenuComponent,
        CdkScrollable
    ]
})
export class AppComponent implements OnInit, OnDestroy {
    readonly flags = FeatureFlag;
    offersToDealsUpdateAM = false;
    displayDealAlert = false;
    userId = 0;
    aspNetUserId = '';
    // note: adding menu items to this component requires security config settings to be updated in the DB
    // see https://ayadev.atlassian.net/wiki/spaces/ARCH/pages/556335139/Adding+menu-item+to+sidebar

    isWfdAgentEnabled = this._featureManager.isEnabledSignal(FeatureFlag.EnableWfdAgent);
    isAmModuleAccountSearchEnabled = this._featureManager.isEnabledSignal(FeatureFlag.AmModuleAccountSearch);
    isBookedCandidateDashboardEnabled = this._featureManager.isEnabledSignal(
        FeatureFlag.WFD_5376_Booked_Candidate_Dashboard
    );

    isNewGridUrlsEnabled = this._featureManager.isEnabledSignal(FeatureFlag.WFD_5510_New_Grid_Urls);

    isReflectiveOnionContactsEnabled = this._featureManager.isEnabledSignal(FeatureFlag.ReflectiveOnionEnableContacts);
    isTravelContractsFacilityAccountSearchEnabled = this._featureManager.isEnabledSignal(
        FeatureFlag.TravelContractsFacilityAccountSearch
    );

    constructor(
        private readonly _pendoService: PendoService,
        private readonly store: Store,
        private readonly _featureManager: LDFeatureManager,
        @Inject(DOCUMENT) private readonly document: Document,
        private readonly _identityService: IdentityService,
        private readonly router: Router,
        private readonly previousRouteService: PreviousRouteService,
        private readonly userMonitoringService: UserMonitoringService,
        private readonly _connectionQualityService: ConnectionQualityService
    ) {}

    @HostListener('document:visibilitychange', ['$event'])
    visibilitychange() {
        this.checkHubConnection();
    }

    checkHubConnection() {
        if (this.document) {
            if (
                this.document.visibilityState === 'hidden' &&
                !this.router.url.toLocaleLowerCase().includes('recruiting/rec-dashboard')
            ) {
                this.store.dispatch(HubActions.disconnectFromCoreHub());
            } else if (this.document.visibilityState === 'visible') {
                let isActive = false;
                this.store
                    .select(selectHubConnectionActive)
                    .pipe(take(1), untilDestroyed(this))
                    .subscribe((data: boolean) => {
                        if (data) {
                            isActive = data;
                        }
                    });
                if (!isActive) {
                    this.store.dispatch(HubActions.connectToCoreHub());
                }
                const identity = this._identityService.current;
                if (identity) {
                    this.userId = identity.contactId;
                    this.aspNetUserId = identity.userId;
                    if (this.userId) {
                        this.store.dispatch(
                            RecruitingActions.DealsActions.getCandidateOffersRecruiterReviewCount({
                                recruiterId: this.userId
                            })
                        );
                    }
                    if (this.aspNetUserId) {
                        this.store.dispatch(InAppNotificationsActions.getUnreadInAppNotificationsCount());
                    }
                }
            }
        }
    }

    ngOnInit() {
        this.userMonitoringService.init();
        this._identityService.isAuthenticated$
            .pipe(
                filter((auth) => auth),
                take(1)
            )
            .subscribe(() => this._pendoService.init());

        this.checkHubConnection();
        this.displayDealAlert = true;

        this.router.events
            .pipe(
                filter((evt: any) => evt instanceof RoutesRecognized),
                pairwise(),
                untilDestroyed(this)
            )
            .subscribe((events: RoutesRecognized[]) => {
                const previousUrl = events[0].urlAfterRedirects;
                this.previousRouteService.addPreviousUrl(previousUrl);
            });

        this._connectionQualityService.initialize();
    }

    ngOnDestroy(): void {
        this.store.dispatch(HubActions.disconnectFromCoreHub());
    }

    private _isLdFeatureFlagEnabled(featureName: FeatureFlag): boolean {
        let isFeatureEnabled = false;
        this._featureManager
            .isEnabled(featureName)
            .pipe(untilDestroyed(this))
            .subscribe((flagIsEnabled) => {
                isFeatureEnabled = flagIsEnabled;
            });
        return isFeatureEnabled;
    }
}
